import { useCallback, useEffect, useMemo, useState } from "react";
import {
  RcaClient,
  RcaApiError,
  type AuthConfig,
  type Me,
  type Scope,
} from "@rca/sdk";
import {
  ApiKeysView,
  AuditView,
  BundlesView,
  CredentialsView,
  EnvironmentsView,
  LogsView,
  MetricsView,
  RunDetailView,
  ProjectsView,
  RunsView,
  TenantsView,
} from "./admin/AdminViews.js";
import { ChatView } from "./chat/ChatView.js";
import { ApiDocsView } from "./docs/ApiDocsView.js";
import { DeployView } from "./deploy/DeployView.js";
import { mustChooseHosting, useHosting } from "./deploy/useHosting.js";
import { SupportView } from "./docs/SupportView.js";
import { CloudAccessView } from "./CloudAccessView.js";
import { usePlatformCloudAccess } from "./usePlatformCloudAccess.js";
import { buildPath, parseRoute, type Route, type Tab } from "./routing.js";
import { createTenantApi } from "./tenantApi.js";
import { BlurFade, DotPattern, ShineBorder, Skeleton } from "./ui/magic.js";

/**
 * アプリのシェル（W6-1）。
 *
 * ログイン方法は `/v1/auth/config` を読んで決める。
 * Zitadel が設定されていれば OIDC、無ければ API キー入力にする。
 * これでローカル開発でも同じ UI を触れる（FR-W8.4）。
 */

/**
 * サイドバー。
 *
 * ★ **1 箇所に置く。** 置き場所を決める前の画面でも同じものを出すので、
 * 複製すると必ずずれる（飛び先や `aria-current` が片方だけ古くなる）。
 */
function Sidebar({
  tabs,
  current,
  onNavigate,
  children,
}: {
  readonly tabs: readonly Tab[];
  readonly current: Tab;
  readonly onNavigate: (tab: Tab) => void;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <aside className="sidebar">
      <h1>Remote Coding Agent</h1>
      <nav aria-label="画面">
        {tabs.map((value) => (
          <button
            key={value}
            type="button"
            aria-current={current === value ? "page" : undefined}
            onClick={() => onNavigate(value)}
          >
            {TAB_LABEL[value]}
          </button>
        ))}
      </nav>
      <div className="spacer" />
      <div className="who">{children}</div>
    </aside>
  );
}

const ADMIN_TABS: readonly Tab[] = [
  "logs",
  "metrics",
  "audit",
  "keys",
  "credentials",
  "environments",
  "bundles",
  "projects",
  "tenants",
];

const TAB_LABEL: Record<Tab, string> = {
  chat: "チャット",
  runs: "Runs",
  api: "API",
  logs: "ログ",
  metrics: "メトリクス",
  audit: "監査ログ",
  keys: "API キー",
  credentials: "Credential",
  environments: "Environment",
  bundles: "資産バンドル",
  projects: "プロジェクト",
  tenants: "テナント",
  support: "使い方",
  "cloud-access": "クラウド接続",
};

export interface AppProps {
  /** テストから差し替えるための注入口。 */
  readonly fetchFn?: typeof globalThis.fetch;
  readonly location?: { pathname: string; search: string; origin: string };
  readonly redirect?: (url: string) => void;
  /** 経路の書き換え。省略するとブラウザの履歴を使う。 */
  readonly pushPath?: (path: string) => void;
}

export function App(props: AppProps = {}): React.JSX.Element {
  const [token, setToken] = useState<string | null>(null);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  /** 自分の情報を引き直す合図（テナントを切り替えたとき）。 */
  const [reloadKey, setReloadKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  /**
   * いまいる場所とタブ。
   *
   * ★ 状態ではなく **URL** を正とする。状態で持つと、画面を共有できず、
   * 再読み込みで失われ、戻るボタンが効かない。
   */
  const initialPath = props.location?.pathname ?? globalThis.location?.pathname ?? "/";
  const [route, setRoute] = useState<Route>(() => parseRoute(initialPath));

  // 戻る・進むに追従する。
  useEffect(() => {
    if (props.pushPath !== undefined) return undefined;
    const onPop = (): void => setRoute(parseRoute(globalThis.location?.pathname ?? "/"));
    globalThis.addEventListener?.("popstate", onPop);
    return () => globalThis.removeEventListener?.("popstate", onPop);
  }, [props.pushPath]);

  const navigate = useCallback(
    (next: Partial<Route>) => {
      setRoute((current) => {
        const merged: Route = { ...current, ...next };
        const path = buildPath(merged);
        if (props.pushPath !== undefined) props.pushPath(path);
        else globalThis.history?.pushState?.({}, "", path);
        return merged;
      });
    },
    [props.pushPath],
  );

  const meTenantId = me?.tenantId ?? null;

  const tab = route.tab;
  const selectedRun = route.runId;

  /**
   * ログインしているテナントを URL に載せる。
   *
   * ★ `replaceState` を使う。ここで履歴を増やすと、戻るボタンで
   * 「テナントの無い URL」に戻ってまた書き換わる、という往復になる。
   */
  useEffect(() => {
    if (meTenantId === null || route.tenantId === meTenantId) return;
    setRoute((current) => {
      const next = { ...current, tenantId: meTenantId };
      if (props.pushPath === undefined) {
        globalThis.history?.replaceState?.({}, "", buildPath(next));
      }
      return next;
    });
  }, [meTenantId, route.tenantId, props.pushPath]);

  /**
   * 共通基盤の口。
   *
   * ★ **置き場所の情報は、必ずここから取る。** 置き場所の宛先を
   * 決めるのに置き場所へ聞く、という輪になってしまう。認証と
   * 置き場所（`/v1/hosting`、`/v1/auth/*`）は共通基盤にしか無い。
   */
  const platformClient = useMemo(
    () =>
      new RcaClient({
        getToken: () => token,
        ...(props.fetchFn === undefined ? {} : { fetch: props.fetchFn }),
        onUnauthorized: () => {
          setToken(null);
          setMe(null);
        },
      }),
    [props.fetchFn, token],
  );

  /**
   * 置き場所。
   *
   * ★ **宛先を決めるより先に引く。** 話しかける先がここで決まるので、
   * 順番が逆だと輪になる。
   */
  const hosting = useHosting(platformClient, me?.tenantId ?? null);

  /**
   * どこへ話しかけるか。
   *
   * ★ **置き場所が自分のクラウドなら、そちらへ直に。** 共通基盤を
   * 通すと顧客のデータが我々を通る（設計 §9）。宛先を切り替えないと、
   * **共通基盤が自分で走らせてしまう**（実機で分かった）。
   *
   * ★ **共通基盤のセッションを他所へ送らない。** 代わりに宛先つきの
   * 短命トークンを共通基盤から貰う（`tenantApi` の中）。
   */
  const tenantApi = useMemo(
    () =>
      createTenantApi({
        apiBaseUrl: hosting.view?.hosting?.apiBaseUrl ?? null,
        sessionToken: token,
        ...(props.fetchFn === undefined ? {} : { fetch: props.fetchFn }),
      }),
    [hosting.view?.hosting?.apiBaseUrl, props.fetchFn, token],
  );

  const client = useMemo(
    () =>
      new RcaClient({
        baseUrl: tenantApi.baseUrl,
        getToken: () => tenantApi.getToken(),
        ...(props.fetchFn === undefined ? {} : { fetch: props.fetchFn }),
        onUnauthorized: () => {
          setToken(null);
          setMe(null);
        },
      }),
    [props.fetchFn, tenantApi],
  );

  /**
   * ★ 一覧は 1 回だけ引き、セレクトとパンくずで使い回す。
   *
   * ★ **早期 return より前**に置くこと。ログイン画面では `me` が null で
   * 描画が分岐するので、後ろに置くと Hook の呼び出し順が変わって壊れる。
   * ログイン前は引かない（401 になるだけ）。
   */
  const projects = useProjects(me === null ? null : client);

  // 認証方法を読む（認証不要）。
  useEffect(() => {
    client
      .auth.config()
      .then(setAuthConfig)
      .catch((cause: unknown) => setError(String(cause)));
  }, [client]);

  // OIDC のコールバック処理。
  const location = props.location ?? {
    pathname: globalThis.location?.pathname ?? "/",
    search: globalThis.location?.search ?? "",
    origin: globalThis.location?.origin ?? "http://localhost",
  };
  /**
   * ★ 認可の完了はサーバ側（BFF）が行う。
   *
   * ブラウザは戻ってくるだけ。失敗したときだけ `?authError=` が付くので、
   * それを拾って見せる。トークン交換の材料はブラウザに一切持たない。
   */
  useEffect(() => {
    const reason = new URLSearchParams(location.search).get("authError");
    if (reason !== null && reason !== "") setError(reason);
  }, [location.search]);

  /**
   * ★ 組織が決まらなかったときの選択肢。
   *
   * 複数組織に属していると、どのテナントとして入るか決められない。
   * 選ぶ画面がログイン後にしか無いと**永久に入れない**ので、
   * ログイン画面でも選べるようにする。
   */
  /**
   * 働くテナントを切り替える。
   *
   * ★ **IdP へ行かせない。** かつては `/auth/login?org=<id>` へ飛ばして
   * いた。付くスコープ `urn:zitadel:iam:org:id:<id>` は「この人の
   * アカウントがその組織にある」の意味なので、Zitadel は利用者を
   * **その組織の中だけ**で探す。アカウントは自組織にあり、相手組織には
   * **権限だけ**がある人は見つからず「ユーザーが見つかりません」で
   * 終わった（本番で踏んだ）。切り替えはサーバ側の Cookie だけで済む。
   *
   * ★ 済んだら**聞き直す**。ロールは組織ごとに違うので、据え置くと
   * admin 限定の画面が出たまま／出ないままになる。
   */
  const switchTenant = useCallback(
    (tenantId: string) => {
      const doFetch = props.fetchFn ?? ((...args: Parameters<typeof globalThis.fetch>) =>
        globalThis.fetch(...args));
      void doFetch("/auth/tenant", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ tenantId }),
      })
        .then((res) => {
          if (!res.ok) throw new Error("テナントを切り替えられませんでした");
          setReloadKey((value) => value + 1);
        })
        .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : String(cause)));
    },
    [props.fetchFn],
  );

  /**
   * 入り直す。
   *
   * ★ **手元の印とサーバの Cookie を両方落とす。** 片方だけだと、
   * 画面は入っていないのにサーバは入ったまま（またはその逆）になる。
   */
  const logout = useCallback(() => {
    setToken(null);
    const doFetch = props.fetchFn ?? ((...args: Parameters<typeof globalThis.fetch>) =>
      globalThis.fetch(...args));
    void doFetch("/auth/logout", { method: "POST" }).catch(() => undefined);
  }, [props.fetchFn]);

  /**
   * 置き場所。
   *
   * ★ **ログインしてから引く。** 認証が要る口なので、先に引くと 401 が
   * 出て「まだ選んでいない」と読み違える。
   */

  /**
   * 基盤の設定（クラウドに入るための、我々の側の資格）。
   *
   * ★ **「見せてよいか」をサーバに聞く。** 画面の側で「このテナントなら
   * 出す」と判断すると、判断が 2 か所になり片方だけ緩む。口が 403 / 404 を
   * 返したら、その人には無いものとして扱う（口の側が本物の関所）。
   */
  const cloudAccess = usePlatformCloudAccess(client, me !== null);

  /**
   * 自分の情報を取る。
   *
   * ★ **トークンの有無で判定しない。** BFF ではブラウザがトークンを持たず、
   * 認証は HttpOnly Cookie で通る。持っているかで分岐すると、
   * ログインしても永久にログイン画面のままになる（本番で踏んだ）。
   * 通るかどうかはサーバに聞く。
   */
  useEffect(() => {
    let alive = true;
    // ★ 認証は共通基盤にしかない。置き場所に聞きに行かせない。
    platformClient
      .auth.me()
      .then((value: Me) => {
        if (!alive) return;
        setMe(value);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (!alive) return;
        setMe(null);
        // 未ログインは異常ではない。ログイン画面を出すだけで、エラーは見せない。
        if (cause instanceof RcaApiError && cause.status === 401) return;
        setError(cause instanceof RcaApiError ? cause.message : String(cause));
      });
    return () => {
      alive = false;
    };
  }, [client, token, reloadKey]);

  /**
   * ★ 認可はサーバ側（BFF）で行う。
   *
   * 以前はブラウザで `code_verifier` を持ち、トークン交換までしていた。
   * いまはサーバへ送り出すだけで、ブラウザはアクセストークンを持たない
   *（HttpOnly Cookie なので JavaScript からは読めない）。
   */
  const startOidcLogin = useCallback(
    (organizationId?: string) => {
      const url = new URL("/auth/login", location.origin);
      url.searchParams.set("returnTo", location.pathname);
      if (organizationId !== undefined && organizationId !== "") {
        url.searchParams.set("org", organizationId);
      }
      (props.redirect ?? ((target: string) => globalThis.location.assign(target)))(url.toString());
    },
    [location.origin, location.pathname, props.redirect],
  );

  if (me === null) {
    return (
      <LoginView
        authConfig={authConfig}
        error={error}
        federatedOrganizations={
          authConfig?.oidc.enabled === true ? (authConfig.oidc.organizations ?? []) : []
        }
        onApiKey={setToken}
        onOidcLogin={(organizationId) => startOidcLogin(organizationId)}
      />
    );
  }

  /**
   * ★ **置き場所が決まるまでメニューを出さない**（要件 FR-1）。
   *
   * 出すと「作ったのに動かない」が起きる。ここが早期 return なので、
   * この下のタブもサイドバーも描かれない。
   *
   * ★ フックはこの上に置くこと（早期 return の下に書くと呼ぶ順が変わる）。
   */
  /**
   * 切り替え先の候補。
   *
   * ★ **トークンから来るものを優先する。** 権限のある組織は Zitadel の
   * ロールのクレームに入っているので、設定に書き出す必要が無い
   *（書くと、増えるたびに配備し直すことになる）。
   * サーバ設定は、クレームに組織が乗らない構成のための控え。
   *
   * ★ **早期 return より上に置く。** 置き場所を選ぶ画面でも切り替えを
   * 出すので、下に書くとそこから見えない。
   */
  const switchableOrganizations =
    me.organizations ??
    (authConfig?.oidc.enabled === true ? (authConfig.oidc.organizations ?? []) : []);

  if (mustChooseHosting(hosting.view)) {
    /*
      ★ **ここでも居場所を変えられること。**

      タブを出さないのは要件どおりだが、**テナントの切り替えと
      ログアウトまで消していた**——権限のある別のテナントへ移ることも、
      入り直すこともできない**行き止まり**だった（利用者に指摘されて
      気付いた）。置き場所は**テナントごと**に決めるので、まだ決めて
      いないテナントに入ると、この画面から出られなくなる。

      ★ プロジェクトの切り替えは**出さない**。まだ何も作れないので、
      選ぶ意味が無い。
    */
    const who = (
      <IdentityPanel
        me={me}
        organizations={switchableOrganizations}
        onSwitch={switchTenant}
        onLogout={logout}
      />
    );
    const deploy = (
      <DeployView
        view={hosting.view!}
        verify={hosting.verify}
        busy={hosting.busy}
        onChoose={(cloud, target) => void hosting.choose(cloud, target)}
        onVerify={() => void hosting.check()}
        onEnter={() => void hosting.reload()}
        canChoose={me.role === "admin"}
      />
    );

    /**
     * ★ **決める前に開けるのは、基盤の設定だけ。**
     *
     * 選択肢は「既定 ＋ 登録済みのクラウド」で組み立てるので、
     * クラウド接続に触れないと**選択肢が増えない**。全部隠していたので、
     * 基盤テナント自身がまだ決めていないと
     * 「入れない → 登録できない → 既定を選ぶしかない（取り消せない）」
     * という**出口の無い輪**になっていた。
     *
     * ★ 他のタブは伏せたまま。資格情報などは**置き場所の保管庫**に入るので、
     * 決める前に入れると、自分のクラウドへ移った瞬間に取り残される。
     *
     * ★ 出すかどうかは**口に聞く**（`available`）。画面の側で
     * 「基盤テナントなら」と判断すると、判断が 2 か所になる。
     */
    const preTabs: Tab[] = cloudAccess.available ? ["cloud-access"] : [];
    if (preTabs.length === 0) {
      // ★ 空の並びを出さない。押せるものが無いサイドバーは邪魔なだけ。
      return (
        <div className="deploy-shell">
          <div className="deploy-who who">{who}</div>
          {deploy}
        </div>
      );
    }

    return (
      <div className="app">
        <a className="skip-link" href="#main">
          本文へ
        </a>
        <Sidebar
          tabs={preTabs}
          current={tab}
          onNavigate={(value) => navigate({ tab: value, runId: null })}
        >
          {who}
        </Sidebar>
        <main className="main" id="main" tabIndex={-1}>
          {tab === "cloud-access" ? (
            <CloudAccessView
              items={cloudAccess.items}
              busy={cloudAccess.busy}
              error={cloudAccess.error}
              onSave={(cloud, setting) => void cloudAccess.save(cloud, setting)}
              onRemove={(cloud) => void cloudAccess.remove(cloud)}
            />
          ) : (
            deploy
          )}
        </main>
      </div>
    );
  }

  /** テナントの表示名。組織のクレームに載っていればそれを使う。 */
  const tenantName =
    switchableOrganizations.find((org) => org.id === me.tenantId)?.name ?? me.tenantId;


  /**
   * いまいる場所。
   *
   * ★ **URL のプロジェクトを使う。** かつては認証主体の `projectId` を
   * 見ていたが、API キーにも Zitadel のトークンにも入らないので
   * 常に null になり、プロジェクトの設定が一度も効かなかった。
   */
  const place = { projectId: route.projectId };


  const scope: Scope = {
    tenantId: me.tenantId,
    ...(route.projectId === null ? {} : { projectId: route.projectId }),
  };
  // API ドキュメントは admin 限定にしない。
  // `/openapi.json` 自体が認証不要で、隠しても意味が無い。
  const tabs: Tab[] =
    me.role === "admin"
      ? [
          "chat",
          "runs",
          "api",
          "support",
          ...ADMIN_TABS,
          // ★ 基盤の管理者にしか出ない。口が「触れない」と言えば出さない。
          ...(cloudAccess.available ? (["cloud-access"] as Tab[]) : []),
        ]
      : ["chat", "runs", "api", "support"];

  return (
    <div className="app">
      {/*
        ★ **本文へ飛ぶ道。**

        サイドバーのタブは admin で 13 個ある。キーボードだけで使う人は、
        画面を開くたびにそれを全部抜けないと本文に届かない。

        ★ **並びの先頭に置く。** 後ろに置くと、そこへ辿り着くまでに
        抜けたいものを抜けてしまう。見えるのは焦点が当たったときだけ。
      */}
      <a className="skip-link" href="#main">
        本文へ
      </a>
      <aside className="sidebar">
        <h1>Remote Coding Agent</h1>
        <nav>
          {tabs.map((value) => (
            <button
              key={value}
              type="button"
              aria-current={tab === value ? "page" : undefined}
              onClick={() => navigate({ tab: value, runId: null })}
            >
              {TAB_LABEL[value]}
            </button>
          ))}
        </nav>
        <div className="spacer" />
        <div className="who">
          <IdentityPanel
            me={me}
            organizations={switchableOrganizations}
            onSwitch={switchTenant}
            onLogout={logout}
          >
            {/* いまいる場所（プロジェクト）の切り替え。
                ★ 既定は「テナント全体」。プロジェクトを選ぶと、
                Environment・バンドル・Credential の一覧と作成先がその中に移る
                （テナントのものは引き続き見える）。
                ★ **置き場所を選ぶ画面には出さない**——まだ何も作れないので、
                プロジェクトを選ぶ意味が無い。 */}
            <PlacePicker
              client={client}
              projects={projects}
              projectId={route.projectId}
              onChange={(projectId) => navigate({ projectId, runId: null })}
            />
          </IdentityPanel>
        </div>
      </aside>

      {/* ★ `tabIndex={-1}` を付けるのは、飛んだ先で焦点を受け取れるように
          するため。無いと、押しても読み上げの位置が動かない。 */}
      <main className="main" id="main" tabIndex={-1}>
        {/* ★ いまどこにいるか。プロジェクトの中では戻る口も兼ねる。 */}
        <PlaceBar
          tenantName={tenantName}
          projectId={route.projectId}
          projects={projects}
          onLeave={() => navigate({ projectId: null, runId: null })}
        />

        {error !== null && (
          <p className="error-banner" role="alert">
            {error}
          </p>
        )}

        {tab === "chat" && (
          <ChatView client={client} getToken={() => token} scope={scope} />
        )}
        {tab === "runs" &&
          (selectedRun === null ? (
            <RunsView client={client} onSelect={(runId) => navigate({ runId })} />
          ) : (
            <RunDetailView
              client={client}
              runId={selectedRun}
              onBack={() => navigate({ runId: null })}
            />
          ))}
        {tab === "api" && <ApiDocsView client={client} />}
        {tab === "support" && (
          <SupportView
            /**
             * ★ **テナントの API の宛先を出す。**
             *
             * `location.origin` は**共通基盤の URL**。置き場所が分かれると、
             * 他のクラウドのテナントに別の場所をコピペさせることになる
             *（見張りは `packages/web/test/security.test.ts`）。
             */
            baseUrl={hosting.view?.hosting?.apiBaseUrl ?? location.origin}
            tenantId={me.tenantId}
            projectId={route.projectId}
            onOpenTab={(next) => navigate({ tab: next, runId: null })}
          />
        )}
        {tab === "cloud-access" && (
          <CloudAccessView
            items={cloudAccess.items}
            busy={cloudAccess.busy}
            error={cloudAccess.error}
            onSave={(cloud, setting) => void cloudAccess.save(cloud, setting)}
            onRemove={(cloud) => void cloudAccess.remove(cloud)}
          />
        )}
        {tab === "logs" && <LogsView client={client} />}
        {tab === "metrics" && <MetricsView client={client} />}
        {tab === "audit" && <AuditView client={client} />}
        {tab === "keys" && <ApiKeysView client={client} />}
        {tab === "credentials" && <CredentialsView client={client} place={place} />}
        {tab === "environments" && (
          <EnvironmentsView client={client} place={place} userId={me.userId ?? me.subject} />
        )}
        {tab === "bundles" && <BundlesView client={client} place={place} />}
        {tab === "projects" && (
          <ProjectsView
            client={client}
            currentProjectId={route.projectId}
            onOpen={(projectId) => navigate({ projectId })}
          />
        )}
        {tab === "tenants" && <TenantsView client={client} />}
      </main>
    </div>
  );
}

/**
 * 貼られた値が LLM の API キーに見えるか。
 *
 * プラットフォームのキー（`rca_`）と LLM のキー（`sk-ant-…` など）は
 * 役割がまったく違うので、取り違えをサーバに投げる前に指摘する。
 */
export function looksLikeLlmKey(value: string): boolean {
  return /^(sk-|xai-|AIza|ya29\.)/.test(value.trim());
}

export function LoginView({
  authConfig,
  error,
  federatedOrganizations = [],
  onApiKey,
  onOidcLogin,
}: {
  authConfig: AuthConfig | null;
  error: string | null;
  /**
   * 外部 IdP へ振るための組織。
   *
   * ★ **サーバの設定から来るものだけ。** ここは未認証の相手が見る画面
   * なので、テナント表の顧客名は出さない。用途は
   * 「その組織に連携（外部 IdP）が設定されていて、名前の画面を経ずに
   * そこへ送りたい」場合だけ——**働くテナントの選択ではない**。
   */
  federatedOrganizations?: readonly { id: string; name: string }[];
  onApiKey: (token: string) => void;
  /** 選んだ組織 ID を渡す。選択肢が無いときは undefined。 */
  onOidcLogin: (organizationId?: string) => void;
}): React.JSX.Element {
  const [value, setValue] = useState("");
  const [hint, setHint] = useState<string | null>(null);


  return (
    <div className="login-page">
      {/* ★ 装飾を置くのは入口だけ。中に入ったら業務画面に徹する。 */}
      <DotPattern gap={18} />

      <BlurFade className="login stack">
        <h1 style={{ fontSize: 20, margin: 0 }}>Remote Coding Agent</h1>

      {error !== null && (
        <p className="error-banner" role="alert">
          {error}
        </p>
      )}

      {authConfig === null ? (
        <Skeleton rows={2} />
      ) : (
        <div className="stack">
          {federatedOrganizations.length > 0 && (
            <div className="stack card">
              <label htmlFor="choose-org">組織を選ぶ</label>
              <select
                id="choose-org"
                defaultValue=""
                onChange={(e) => onOidcLogin(e.target.value)}
              >
                <option value="" disabled>
                  （選んでください）
                </option>
                {federatedOrganizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
              <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                自社の認証を使う場合は、組織を選んでください。
              </p>
            </div>
          )}

          {authConfig.oidc.enabled && (
            <div className="stack card" style={{ position: "relative", overflow: "hidden" }}>
              {/* 唯一の入口なので、ここだけ目を引かせる。 */}
              <ShineBorder duration={10} color={["var(--link)", "var(--primary)"]} />
              <button
                type="button"
                className="primary"
                onClick={() => onOidcLogin()}
                style={{ alignSelf: "flex-start" }}
              >
                シングルサインオンでログイン
              </button>
            </div>
          )}

          {authConfig.apiKeyEnabled && (
            <form
              className="stack card"
              onSubmit={(e) => {
                e.preventDefault();
                const entered = value.trim();
                if (entered === "") return;
                if (looksLikeLlmKey(entered)) {
                  setHint(
                    "これは LLM の API キーのようです。ここに入れるのはこの基盤のキー（rca_ で始まる）です。" +
                      "LLM のキーはログイン後の「Credential」画面で登録してください。",
                  );
                  return;
                }
                setHint(null);
                onApiKey(entered);
              }}
            >
              <label htmlFor="api-key">プラットフォーム API キー</label>
              <input
                id="api-key"
                type="password"
                placeholder="rca_..."
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
              <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                この基盤にログインするためのキーです（<code>rca_</code> で始まります）。
                管理者が「API キー」画面で発行します。
                ローカル開発サーバなら、起動時のログに表示されています。
              </p>
              <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                <strong>LLM の API キー（<code>sk-ant-…</code> など）ではありません。</strong>
                そちらはログイン後に「Credential」画面で登録します。
              </p>
              <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                キーはこのタブのメモリにだけ置かれます。リロードすると再入力が必要です。
              </p>
              {hint !== null && (
                <p className="error-banner" role="alert">
                  {hint}
                </p>
              )}
              <button type="submit" disabled={value.trim() === ""}>
                続行
              </button>
            </form>
          )}
        </div>
      )}
      </BlurFade>
    </div>
  );
}

/**
 * いまいる場所（プロジェクト）を選ぶ。
 *
 * ★ 選択は URL に載る。ここで持つのは候補の一覧だけ。
 * プロジェクトが 1 つも無いテナントでは**何も出さない**——
 * 選べないものを見せても迷わせるだけ。
 */
export function PlacePicker({
  client,
  projectId,
  onChange,
  projects: given,
}: {
  client: RcaClient;
  projectId: string | null;
  onChange: (projectId: string | null) => void;
  /** 一覧。省略すると自分で引く（単体で使うときのため）。 */
  projects?: readonly PlaceProject[] | null;
}): React.JSX.Element | null {
  const fetched = useProjects(given === undefined ? client : null);
  const projects = given === undefined ? fetched : given;

  if (projects === null || projects.length === 0) return null;

  return (
    <div className="stack" style={{ marginTop: 8 }}>
      <label htmlFor="switch-place">プロジェクト</label>
      <select
        id="switch-place"
        value={projectId ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
      >
        <option value="">（テナント全体）</option>
        {/* 消えたプロジェクトの URL を開いたときも、いまの選択を示す。 */}
        {projectId !== null && !projects.some((p) => p.id === projectId) && (
          <option value={projectId}>{projectId}（不明）</option>
        )}
        {projects.map((project) => (
          <option key={project.id} value={project.id}>
            {project.name}
          </option>
        ))}
      </select>
    </div>
  );
}

export interface PlaceProject {
  readonly id: string;
  readonly name: string;
}

/**
 * プロジェクトの一覧を 1 回だけ引く。
 *
 * `client` が null なら引かない（呼び出し側が一覧を持っているとき）。
 */
export function useProjects(client: RcaClient | null): PlaceProject[] | null {
  const [projects, setProjects] = useState<PlaceProject[] | null>(null);

  useEffect(() => {
    if (client === null) return undefined;
    let alive = true;
    client.projects
      .list()
      .then((page) => {
        if (alive) setProjects(page.items.map((p) => ({ id: p.id, name: p.name })));
      })
      // 権限が無い・古いサーバなど。選択肢が出ないだけで、他は動く。
      .catch(() => {
        if (alive) setProjects([]);
      });
    return () => {
      alive = false;
    };
  }, [client]);

  return projects;
}

/**
 * いまいる場所を、本文の一番上に出す。
 *
 * ★ サイドバーのセレクトだけでは足りなかった。プロジェクトに入ったあと
 * 「自分がどこにいるか分からない」「戻る口が見つからない」という状態になる。
 * パンくずは**現在地の表示**と**戻る口**を同時に satisfy する。
 */
export function PlaceBar({
  tenantName,
  projectId,
  projects,
  onLeave,
}: {
  tenantName: string;
  projectId: string | null;
  projects: readonly PlaceProject[] | null;
  onLeave: () => void;
}): React.JSX.Element {
  const project = projects?.find((p) => p.id === projectId) ?? null;

  if (projectId === null) {
    return (
      <div className="place-bar" data-testid="place-bar">
        <span className="here">{tenantName}</span>
        <span className="muted">テナント全体</span>
      </div>
    );
  }

  return (
    <div className="place-bar in-project" data-testid="place-bar">
      {/* 上位はここへ戻る口を兼ねる。 */}
      <button type="button" className="linklike root" onClick={onLeave}>
        {tenantName}
      </button>
      <span className="sep">›</span>
      {/* 名前が引けないのは、消えた ID の URL を開いたとき。 */}
      <span className="here">{project?.name ?? projectId}</span>
      <span className="badge accent">プロジェクト</span>
      <span className="spacer" />
      <button type="button" onClick={onLeave}>
        テナント全体へ戻る
      </button>
    </div>
  );
}

/**
 * いま誰として、どのテナントに居るか。切り替えと入り直しも。
 *
 * ★ **どの画面にも出す。** かつてはサイドバーの中に書いていたので、
 * 置き場所を選ぶ画面（サイドバーごと差し替わる）では**テナントの
 * 切り替えもログアウトも消えていた**。置き場所は**テナントごと**に
 * 決めるので、まだ決めていないテナントに入ると**そこから出られない**
 * ——権限のある別のテナントへも移れず、入り直すこともできない。
 *
 * ★ **中身は画面によって変わる。** プロジェクトの切り替えは、まだ何も
 * 作れない画面には出さない（`children` で受ける）。
 */
export function IdentityPanel({
  me,
  organizations,
  onSwitch,
  onLogout,
  children,
}: {
  readonly me: Me;
  readonly organizations: readonly { readonly id: string; readonly name: string }[];
  readonly onSwitch: (tenantId: string) => void;
  readonly onLogout: () => void;
  readonly children?: React.ReactNode;
}): React.JSX.Element {
  return (
    <>
      <div>{me.displayName ?? me.subject}</div>
      <div className="mono">
        {me.tenantId} / {me.role}
      </div>

      {children}

      {/* ★ 組織（テナント）の切り替え。
          一覧を出すのは**ログイン後だけ**——ログイン前に出すと、
          認証していない相手に顧客名を晒すことになる。
          ★ 切り替え先が無ければ出さない（空の欄を見せない）。 */}
      {organizations.length > 0 && (
        <div className="stack" style={{ marginTop: 8 }}>
          <label htmlFor="switch-org">組織</label>
          <select id="switch-org" value={me.tenantId} onChange={(e) => onSwitch(e.target.value)}>
            {organizations.some((org) => org.id === me.tenantId) ? null : (
              <option value={me.tenantId}>{me.tenantId}（いま）</option>
            )}
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <button type="button" onClick={onLogout} style={{ marginTop: 8 }}>
        ログアウト
      </button>
    </>
  );
}
